import dynamicRoutes from "@dynamicRoutes";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import { useAuthStore } from "../store/auth";
import { listenForCustomerSessionEvents, useCustomerSession } from "../store/customerSession";
import { useUserStore } from "../store/user";

const sharedRoutes: RouteRecordRaw[] = [
  {
    path: "/auth", //utilizado prefixo auth para sobrescrever a path "/",
    //mas não aparecerá nas rotas children, pois elas começam com "/", portanto ignoram esse prefixo

    redirect: "/login", // caso a rota root "/auth" seja chamada será direcionada para o login
    component: () => import("../components/LayoutAuth.vue"),
    children: [
      {
        path: "/login",
        name: "Login",
        component: () => import("../views/Login.vue"),
        meta: { transition: "slide-left" },
      },
      {
        path: "/redefinicao-de-senha",
        name: "Redefinição senha",
        component: () => import("../components/FormRequestPasswordReset.vue"),
      },
      {
        path: "/redefinir-senha",
        name: "Redefinir senha",
        component: () => import("../views/ResetPassword.vue"),
      },
      {
        path: "/:pathMatch(.*)*",
        name: "Não encontrada",
        component: () => import("../views/Error404.vue"),
      },
    ],
  },
];

const routes =
  import.meta.env.VITE_PROJECT_TYPE === "user"
    ? dynamicRoutes
    : [...sharedRoutes, ...dynamicRoutes];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      const navbarHeight = document.getElementById("navbar")?.clientHeight ?? 0;

      return {
        el: to.hash,
        top: navbarHeight,
        behavior: "smooth",
      };
    }

    return { top: 0 };
  },
});

function generateFlatRoutes(rawRoutes: RouteRecordRaw[], parentPath?: string) {
  const _flatRoutes: RouteRecordRaw[] = [];

  rawRoutes.forEach((route) => {
    if (route.children) {
      _flatRoutes.push(...generateFlatRoutes(route.children, route.path));
    } else {
      const fullPath =
        parentPath && !route.path.startsWith("/")
          ? `${parentPath}/${route.path}`.replace(new RegExp("/{2,}", "gm"), "/")
          : route.path;
      _flatRoutes.push({ ...route, path: fullPath });
    }
  });

  return _flatRoutes;
}

export const flatRoutes = generateFlatRoutes(routes);

let customerEventsStarted = false;

router.beforeEach(async (to) => {
  document.title = to.name ? `Tirss | ${to.name.toString()}` : "Tirss";

  if (import.meta.env.VITE_PROJECT_TYPE === "user") {
    const session = useCustomerSession();
    if (!customerEventsStarted) {
      customerEventsStarted = true;
      listenForCustomerSessionEvents(async (type) => {
        const pending = session.handleExternalEvent(type);
        const generation = session.generation;
        await pending;
        if (generation === session.generation && !session.restoreError)
          await router.replace(session.user ? session.initialRoute : "/login");
      });
    }
    await session.restore();
    if (session.restoreError) return;
    if (!session.user) {
      return to.meta.requiresAuth
        ? to.meta.preserveCustomerRedirect
          ? { path: "/login", query: { redirect: to.fullPath } }
          : "/login"
        : undefined;
    }
    if (session.initialRoute !== "/inicio") {
      return to.path === session.initialRoute ? undefined : session.initialRoute;
    }
    if (
      !to.meta.requiresAuth ||
      to.path === "/confirmar-cadastro" ||
      to.path === "/completar-cadastro"
    )
      return session.initialRoute;

    return;
  }

  if (to.name === "Não encontrada") {
    return;
  }

  const authStore = useAuthStore();
  const userStore = useUserStore();

  if (to.meta.requiresAuth && !authStore.isUserLoggedIn) {
    return "/login";
  }

  await userStore.loadUserData();

  if (
    (!to.meta.requiresAuth && authStore.isUserLoggedIn) ||
    (to.meta.permission && !userStore.activeUser?.permissions?.includes(to.meta.permission))
  ) {
    return userStore.initialLoggedInRoute;
  }
});

export default router;
